import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import headerLogo from '../../../img/syndicate_logo2.webp';
import { useClearance } from '@/app/hooks/useClearance';
import { useLang } from '@/app/hooks/useLang';

const NUCLEAR_APOCALYPSE_AT = new Date('2030-01-01T00:00:00Z').getTime();

const formatCountdown = (now: number) => {
  const remainingMs = Math.max(NUCLEAR_APOCALYPSE_AT - now, 0);
  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(days).padStart(3, '0')}D ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

type NavLink = {
  path: string;
  code: string;
};

export function Header() {
  const location = useLocation();
  const { lang, t, toggle } = useLang();
  const { hasClearance, revokeClearance } = useClearance();
  const [menuPath, setMenuPath] = useState<string | null>(null);
  const menuOpen = menuPath === location.pathname;
  const clockRef = useRef<HTMLSpanElement>(null);
  const apocalypseLabel = t('header.apocalypse');

  useEffect(() => {
    const tick = () => {
      const node = clockRef.current;
      if (!node) return;
      const value = formatCountdown(Date.now());
      node.textContent = value;
      node.parentElement?.setAttribute('aria-label', `${apocalypseLabel} ${value}`);
    };
    const start = window.setTimeout(tick, 0);
    const timer = window.setInterval(tick, 1000);
    return () => {
      window.clearTimeout(start);
      window.clearInterval(timer);
    };
  }, [apocalypseLabel]);

  const tickerText = useMemo(() => {
    const pass = [
      'HELLO ATOMIC',
      t('header.overheard.1'),
      t('header.subtitle'),
      '極秘',
      '閲覧禁止',
      '機密',
    ].join(' // ');
    return `${pass} // `.repeat(4);
  }, [t]);

  const navLinks = useMemo<NavLink[]>(
    () => [
      { path: '/', code: t('nav.bunker.code') },
      { path: '/releases', code: t('nav.signal.code') },
      { path: '/roster', code: t('nav.ops.code') },
      { path: '/merch', code: t('nav.supply.code') },
      { path: '/dossiers', code: t('nav.intel.code') },
      { path: '/contact', code: t('nav.channel.code') },
    ],
    [t],
  );

  return (
    <header>
      <div className="classif-strip" aria-hidden="true">
        <div className="classif-strip__track">
          <span className="classif-strip__text">{tickerText}</span>
          <span className="classif-strip__text">{tickerText}</span>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 pt-4 pb-4">
        <div className="term-head">
          <Link to="/" className="term-logo">
            <img
              src={headerLogo}
              alt="Nightmare Syndicate"
              width={240}
              height={68}
              decoding="async"
            />
          </Link>
          <div className="term-status-slot">
            <div className="term-status">
              <div className="term-status__actions">
                {hasClearance ? (
                  <button
                    type="button"
                    onClick={revokeClearance}
                    className="term-status__pill term-status__btn term-status__login"
                  >
                    {t('header.logout')}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="term-status__pill term-status__btn term-status__login"
                  >
                    {t('header.login')}
                  </Link>
                )}
                <button
                  type="button"
                  onClick={toggle}
                  className="term-status__pill term-status__btn"
                  aria-label={lang === 'en' ? 'Switch to Japanese' : '英語に切り替え'}
                >
                  {lang === 'en' ? '日本語' : 'EN'}
                </button>
                <p className="term-status__pill term-status__clock" aria-label={apocalypseLabel}>
                  <span>T-MINUS</span>
                  <span ref={clockRef} className="term-status__v">---D --:--:--</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <nav className="term-nav-row" aria-label="Primary">
          <button
            type="button"
            className={menuOpen ? 'term-nav term-nav-toggle term-nav--active' : 'term-nav term-nav-toggle'}
            aria-expanded={menuOpen}
            aria-controls="primary-menu"
            onClick={() =>
              setMenuPath((current) => (current === location.pathname ? null : location.pathname))
            }
          >
            [{menuOpen ? t('nav.close') : t('nav.menu')}]
          </button>
          <div id="primary-menu" className={menuOpen ? 'term-nav-menu is-open' : 'term-nav-menu'}>
            {navLinks.map((link) => {
              const isActive =
                link.path === '/'
                  ? location.pathname === '/'
                  : location.pathname === link.path || location.pathname.startsWith(`${link.path}/`);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={isActive ? 'term-nav term-nav--active' : 'term-nav'}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => setMenuPath(null)}
                >
                  [{link.code}]
                </Link>
              );
            })}
          </div>
        </nav>
      </div>
    </header>
  );
}
